import { memo, type SyntheticEvent } from "react";
import DOMPurify from "dompurify";
import { MathJax } from "better-react-mathjax";
import { problemTextToHtml, QB_SANITIZE } from "./problem-text";
import { advanceOrChip } from "./image-fallback";


// onErrorCapture, not onError: image load errors do not bubble. DOMPurify also
// strips inline onerror=, so recovery has to come from the wrapper.
function handleImgError(e: SyntheticEvent<HTMLElement>) {
  const el = e.target as HTMLElement | null;
  if (!el || el.tagName !== "IMG") return;
  advanceOrChip(el as HTMLImageElement);
}

/**
 * Renders scraped problem text: inline images, bold/italic, and LaTeX.
 *
 * Keep `memo` and `dynamic` together. `dynamic` alone re-typesets on every
 * parent render; `memo` alone stops prod builds re-typesetting at all, since
 * better-react-mathjax defaults `dynamic` to NODE_ENV !== "production".
 */
export const ProblemText = memo(function ProblemText({
  text,
  className,
  inline = false,
}: {
  text: string | null | undefined;
  className?: string;
  inline?: boolean;
}) {
  const html = DOMPurify.sanitize(problemTextToHtml(text), QB_SANITIZE);
  if (!html) return null;

  return (
    <MathJax dynamic inline={inline} className={className}>
      {/* innerHTML goes on an inner span: MathJax renders children *and*
          spreads unknown props, so putting it on MathJax trips React's
          children-vs-dangerouslySetInnerHTML invariant. */}
      <span onErrorCapture={handleImgError} dangerouslySetInnerHTML={{ __html: html }} />
    </MathJax>
  );
});
